/**
 * Scope construction: pack + scenario + params + user overrides → the numbers
 * an expression actually sees.
 *
 * Layering order matters, and is deliberately narrow-wins:
 *   1. Input.value                  the published default
 *   2. Input.scenarioValues[id]     values that differ by scenario
 *   3. Scenario.inputOverrides      scenario-level overrides
 *   4. Scenario params              e.g. delay_years, bound and sourced
 *   5. User overrides               slider positions, from the URL
 */

import type { Comparator, Input, Num, Pack, Scenario, Undisclosed } from './schema.js';
import { isUndisclosed } from './schema.js';
import type { RangeScope } from './range.js';
import { dimOf } from './units.js';
import { evalNode, parse, type DimScope } from './expr.js';

export interface ScopeState {
  scenarioId: string;
  /** Scenario param id → value (e.g. delay_years → 2.5). */
  params: Record<string, number>;
  /** Input id → user-set value from a slider. */
  overrides: Record<string, number>;
}

export interface BuiltScope {
  /** Values available to expressions. */
  values: RangeScope;
  /** Inputs whose value is not public. Any metric touching one is blocked. */
  undisclosed: Record<string, Undisclosed>;
  /** Which inputs the user has moved off their published default. */
  modified: string[];
  scenario: Scenario;
}

export function inputById(pack: Pack): Record<string, Input> {
  const out: Record<string, Input> = {};
  for (const i of pack.inputs) out[i.id] = i;
  return out;
}

export function scenarioById(pack: Pack, id: string): Scenario {
  const s = pack.scenarios.find((x) => x.id === id);
  if (!s) throw new Error(`Unknown scenario "${id}"`);
  return s;
}

export function buildScope(pack: Pack, state: ScopeState): BuiltScope {
  const scenario = scenarioById(pack, state.scenarioId);
  const values: RangeScope = {};
  const undisclosed: Record<string, Undisclosed> = {};
  const modified: string[] = [];

  for (const input of pack.inputs) {
    // 1 & 2: base value, or the scenario-specific one if declared.
    const scenarioValue = input.scenarioValues?.[scenario.id];
    const base: Num | Undisclosed = scenarioValue ?? input.value;

    if (isUndisclosed(base)) {
      undisclosed[input.id] = base;
      continue;
    }
    values[input.id] = base;
  }

  // 3: scenario-level overrides.
  for (const [id, v] of Object.entries(scenario.inputOverrides)) {
    if (undisclosed[id]) delete undisclosed[id];
    values[id] = v;
  }

  // 4: scenario params become plain symbols expressions can reference.
  for (const p of scenario.params ?? []) {
    const v = state.params[p.id];
    values[p.id] = v ?? p.default;
  }

  // 5: user overrides win, and are recorded so the UI can say so.
  const byId = inputById(pack);
  for (const [id, v] of Object.entries(state.overrides)) {
    const input = byId[id];
    if (!input?.adjustable) continue;
    const clamped = Math.min(Math.max(v, input.adjustable.min), input.adjustable.max);
    const current = values[id];
    const currentMid = typeof current === 'number' ? current : current?.mid;
    if (currentMid !== clamped) modified.push(id);
    // A user-set value is a point, not a band — they picked one number.
    values[id] = clamped;
  }

  return { values, undisclosed, modified, scenario };
}

/** Dimension map for the whole pack, used by the validator. */
export function buildDimScope(pack: Pack): DimScope {
  const out: DimScope = {};
  for (const i of pack.inputs) out[i.id] = dimOf(i.unit);
  for (const s of pack.scenarios) {
    for (const p of s.params ?? []) out[p.id] = dimOf(p.unit);
  }
  // `t` is a plain year index injected into per-year series expressions, not a
  // duration — so it is dimensionless and composes with period counts.
  out['t'] = dimOf('ratio');
  return out;
}

/**
 * The divisor behind a comparison, whichever form the pack declared it in.
 * Returns the central value, or undefined when it cannot be resolved — an
 * undisclosed input in the closure, or a divisor of zero, which would produce
 * an infinite multiple rather than a comparison.
 */
export function comparatorDivisor(
  comparator: Comparator,
  scope: BuiltScope,
): number | undefined {
  let value: number | undefined;

  if (comparator.unitCostExpr) {
    try {
      const mids: Record<string, number> = {};
      for (const [k, v] of Object.entries(scope.values)) {
        mids[k] = typeof v === 'number' ? v : v.mid;
      }
      value = evalNode(parse(comparator.unitCostExpr), mids);
    } catch {
      return undefined;
    }
  } else if (comparator.unitCostInputId) {
    const raw = scope.values[comparator.unitCostInputId];
    if (raw === undefined) return undefined;
    value = typeof raw === 'number' ? raw : raw.mid;
  }

  return value && Number.isFinite(value) ? value : undefined;
}

/** Default param values for a scenario. */
export function defaultParams(scenario: Scenario): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of scenario.params ?? []) out[p.id] = p.default;
  return out;
}
